import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IoMailUnreadOutline } from 'react-icons/io5';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { openMyBetsModal } from '@/store/slices/currentBetCountSlice';
import { cn } from '@/utils';

interface NavItem {
  id: string;
  label: string;
  // Either a class for the site's icon font, or a react-icons component —
  // the font has no real envelope/inbox glyph ("icon-email" renders as an
  // unrelated controller shape), so Inbox uses a react-icons one instead.
  icon: string | React.ComponentType<{ className?: string }>;
  href: string;
}

const navItems: NavItem[] = [
  {
    id: 'home',
    label: 'Home',
    icon: 'icon-home',
    href: '/',
  },
  {
    id: 'casino',
    label: 'Casino',
    icon: 'icon-poker',
    href: '/casino',
  },
  {
    id: 'sports',
    label: 'Sports',
    icon: 'icon-sport',
    href: '/sports/all/inplay',
  },
  {
    id: 'messages',
    label: 'Inbox',
    icon: IoMailUnreadOutline,
    href: '/messages',
  },
  {
    id: 'bets',
    label: 'My Bets',
    icon: 'icon-list',
    href: '/my-bets',
  },
];

export default function BottomNav() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentBetCount = useAppSelector((state) => state.currentBetCount.totalItems);
  const currentBetCountText = currentBetCount > 99 ? '99+' : currentBetCount;
  const unreadMessageCount = useAppSelector((state) => state.userMessage.unreadCount);
  const unreadMessageCountText = unreadMessageCount > 99 ? '99+' : unreadMessageCount;
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 0,
    width: 0,
  });

  const activeIndex = Math.max(
    navItems.findIndex((item) =>
      item.href === '/' ? location.pathname === '/' : location.pathname.startsWith(item.href)
    ),
    0
  );

  const currentIndex = hoveredIndex ?? activeIndex;

  useEffect(() => {
    const currentTab = tabRefs.current[currentIndex];

    if (!currentTab) return;

    setIndicatorStyle({
      left: currentTab.offsetLeft,
      width: currentTab.offsetWidth,
    });
  }, [currentIndex]);

  return (
    <div className="fixed bottom-0 left-1/2 z-50 w-full max-w-[30rem] -translate-x-1/2">
      <div className="relative flex h-[4.625rem] items-center justify-around rounded-t-xl border-t border-zinc-300 bg-white">
        <div
          className="absolute top-[0.313rem] h-[4rem] rounded-lg bg-primary transition-all duration-300 ease-in-out"
          style={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
          }}
        />

        {navItems.map((item, index) => (
          <button
            key={item.id}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            onClick={() => {
              if (item.id === 'bets') {
                dispatch(openMyBetsModal());
                return;
              }
              navigate(item.href);
            }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            className={cn(
              'relative z-10 flex w-[4.5rem] flex-col items-center justify-center rounded-lg py-2 transition-colors'
            )}
          >
            <div className="relative">
              {typeof item.icon === 'string' ? (
                <i className={`${item.icon} text-2xl text-black/80`} />
              ) : (
                <item.icon className="text-2xl text-black/80" />
              )}
              {item.id === 'bets' && currentBetCount !== 0 && (
                <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#e50000] px-1 text-[0.75rem] font-bold leading-none text-white">
                  {currentBetCountText}
                </span>
              )}
              {item.id === 'messages' && unreadMessageCount !== 0 && (
                <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#e50000] px-1 text-[0.75rem] font-bold leading-none text-white">
                  {unreadMessageCountText}
                </span>
              )}
            </div>

            <span className="text-[0.844rem] leading-tight text-black/80 transition-colors">
              {item.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
